import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import {
  dropNullFilterFields,
  type ServerSelectedFilterOption,
} from 'features/filters/utils'
import type { FilterPreset } from 'types/project'

/**
 * The panel fields a saved filter sets when applied. Reads `filters_v2` — the
 * only place a preset saved after the v2-only write change keeps its filters
 * (`criteria` / `search_terms` / `search_criteria` come back as `[]`, and the
 * server sends `filters_v2`'s optional filter fields as JSON `null`).
 *
 * Only what the preset carries is returned: a preset with no `dateRange`
 * leaves the panel's date alone.
 */
export function savedFilterToPanelUpdates(
  preset: FilterPreset,
): Partial<ChartPanel> {
  const state = preset.filters_v2
  // `types/project`'s filter fields are loosely typed `string` (a shared-layer
  // type can't import charts-v2's unions), hence the cast into the server shape.
  const { filters } = dropNullFilterFields({
    filters: state.filters as ServerSelectedFilterOption[],
  })
  return {
    filters: filters as ChartPanel['filters'],
    searchTerms: state.searchTerms,
    searchCriteria: state.searchCriteria,
    // A preset saved before the server kept the flag comes back without it.
    // `searchTerms` only ever holds a smart-search phrase (keywords live in
    // `searchCriteria`), so a phrase means it was on.
    smartSearchEnabled:
      state.smartSearchEnabled ?? state.searchTerms.length > 0,
    ...(state.dateRange ? { dateRange: state.dateRange } : {}),
  }
}
